import { Component, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsInitOpts, EChartsOption, EChartsType, TooltipComponentOption, TooltipComponentPositionCallback } from 'echarts';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import jsPDF from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';
import { ParserService, Measurement, ParserOutput } from '../../services/parser.service';
import { buildRegion, HybridViewMode, RegionMatrices } from '../../../utils/region-utils';
import { MissingSequentialBaselineError } from '../../../utils/baseline-utils'; 
import { buildScalabilityMatrices, buildRelativeScalabilityMatrices } from '../../../utils/scalability-utils';
import { HeatmapExtraFields, HeatmapService, HeatmapParams } from '../../services/heatmap.service';
import { ChartMetadataService } from '../../services/chart-metadata.service';
import { RegionTreeComponent } from '../region-tree/region-tree.component';
import { AnalysisUploadInputComponent } from '../analysis-upload-input/analysis-upload-input.component';
import { AnalysisStateService, RegionSelectionSummary, RegionSelectionRegion } from '../../services/analysisState.service';
import { EMPTY_EXECUTION_DETAILS, ExecutionDetails, ParallelismMode } from '../../models/execution-details.models';
import {
  buildEnergyChartKey,
  ChartDefinition,
  ChartKey,
  ComparisonChart,
  ComparisonChartOptions,
  ComparisonRegionData,
  getEnergySensorName,
  isEnergyChartKey,
  RegionDetails,
  RegionMetrics
} from '../../models/chart.models';

type ChartMode = 'normal' | 'relative' | 'edp';

interface EnergyChartMatrices {
  rawEnergy: number[][];
  normalizedEnergy: number[][];
  rawEdp: number[][];
  normalizedEdp: number[][];
}

interface ChartBuildContext {
  region: RegionMatrices;
  efficiencyMatrix: number[][];
  imbalanceMatrix: number[][];
  strong: number[][];
  scalab: number[][];
  weak: number[][];
  relativeStrong: number[][];
  relativeScalab: number[][];
  relativeWeak: number[][];
  speedupMatrix: number[][];
  lossMatrix: number[][];
  parTime: number[][];
  seqTime: number[][];
  normalizedPerformance: number[][];
  energyByKey: Record<string, EnergyChartMatrices>;
}

interface ExpandedChartState {
  chartKey: ChartKey;
  regionId: string;
  title: string;
  kind: 'single' | 'comparison';
  singleIndex?: number;
  comparisonChart?: ComparisonChart;
  comparisonRegion?: ComparisonRegionData;
}

@Component({
  selector: 'app-upload-json',
  templateUrl: './upload-json.component.html',
  styleUrls: ['./upload-json.component.scss'],
  imports: [
    CommonModule, 
    NgxEchartsModule, 
    RegionTreeComponent,
    AnalysisUploadInputComponent,
    DragDropModule
  ],
  standalone: true
})
export class UploadJsonComponent implements OnInit, OnDestroy {
  @ViewChild(AnalysisUploadInputComponent)
  private uploadInputComponent?: AnalysisUploadInputComponent;

  isFileUploaded = false;
  isDraggingFile = false;
  private resetSubscription: Subscription | undefined;

  // Armazena as instâncias dos gráficos para o download
  private chartInstances: Record<string, any> = {};
  private expandedChartInstance: any = null;

  normalOptions: EChartsOption[] = [];
  normalSmoothedOptions: EChartsOption[] = [];
  relativeOptions: EChartsOption[] = [];
  relativeSmoothedOptions: EChartsOption[] = [];

  showSmoothed: boolean[] = [];
  showRelative: boolean[] = [];
  showEDP: boolean[] = [];

  measurements: Measurement[] = [];
  arguments: string[] = [];
  executionDetails: ExecutionDetails = EMPTY_EXECUTION_DETAILS;
  sensors: string[] = [];
  error = '';
  baselineWarning: string | null = null
  baselineMode: ParallelismMode | null = null
  metricsByRegion: Record<string, RegionMetrics> = {};
  regionDetailsMap: Record<string, RegionDetails> = {};
  hybridViewMode: HybridViewMode = 'total-cores';

  public chartDefinitions: ChartDefinition[] = [];
  // CORREÇÃO: Mapa para escalas individuais por região
  public customMaxSpeedupMap: Record<string, number> = {}; 
  public autoMaxSpeedup: number = 1;
  public autoMaxLoss: number = 1;

  public autoMaxSpeedupMap: Record<string, number> = {};

  availableRegions: string[] = [];
  selectedRegions: string[] = [];
  selectedRegionDetails: Measurement | null = null;
  comparisonCharts: ComparisonChart[] = [];
  expandedChart: ExpandedChartState | null = null;
  expandedChartOption: EChartsOption | null = null;
  private readonly expandedTooltipPosition: TooltipComponentPositionCallback = (
    point,
    _params,
    _el,
    _rect,
    size
  ) => {
    const [x, y] = point;
    const [contentWidth, contentHeight] = size.contentSize;
    const [viewWidth, viewHeight] = size.viewSize;
    const gap = 14;

    const left = Math.min(
      Math.max(x - contentWidth / 2, gap),
      Math.max(gap, viewWidth - contentWidth - gap)
    );

    const preferredTop = y - contentHeight - gap;
    const top = preferredTop < gap
      ? Math.min(y + gap, viewHeight - contentHeight - gap)
      : preferredTop;

    return [left, Math.max(gap, top)];
  };

  constructor(
    private parser: ParserService,
    private heatmap: HeatmapService,
    private chartMetadata: ChartMetadataService,
    private analysisStateService: AnalysisStateService
  ) {}

  ngOnInit(): void {
    this.resetSubscription = this.analysisStateService.reset$.subscribe(() => {
      this.resetAnalysis();
    });
  }

  ngOnDestroy(): void {
    this.resetSubscription?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])onDocumentClick(event: MouseEvent) {
  // Busca todos os elementos <details> que estejam abertos
  const popovers = document.querySelectorAll('details[open]');
  
  popovers.forEach((popover) => {
    
    if (!popover.contains(event.target as Node)) {
      popover.removeAttribute('open');
    }
  });
}

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeExpandedChart();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.expandedChartInstance?.resize();
  }

  private resetAnalysis(): void {
    this.clearAllChartDataAndStates();
    this.measurements = [];
    this.arguments = [];
    this.executionDetails = EMPTY_EXECUTION_DETAILS;
    this.sensors = [];
    this.error = '';
    this.baselineWarning = null;
    this.baselineMode = null;
    this.metricsByRegion = {};
    this.regionDetailsMap = {};
    this.hybridViewMode = 'total-cores';
    this.availableRegions = [];
    this.selectedRegions = [];
    this.selectedRegionDetails = null;
    this.isFileUploaded = false;
    this.customMaxSpeedupMap = {};
    this.analysisStateService.updateRegionSelection(null);
    this.analysisStateService.clearUploadedFile();
    this.uploadInputComponent?.resetInput();
  }

  private clearAllChartDataAndStates(): void {
    this.normalOptions = [];
    this.normalSmoothedOptions = [];
    this.relativeOptions = [];
    this.relativeSmoothedOptions = [];
    this.chartDefinitions = [];
    this.showSmoothed = [];
    this.showRelative = [];
    this.showEDP = [];
    this.comparisonCharts = [];
    this.closeExpandedChart();
  }

  updateSpeedupScale(newMax: string, regionId: string, popover?: HTMLDetailsElement): void {
    /*
    const value = parseFloat(newMax);
    if (!isNaN(value) && value > 0) {
      this.customMaxSpeedupMap[regionId] = value;
      if (this.selectedRegions.length > 0) {
        this.onRegionSelected(this.selectedRegions);
      }
    } else {
      delete this.customMaxSpeedupMap[regionId];
      this.onRegionSelected(this.selectedRegions);
    }
    */

    const value = parseFloat(newMax);

    if (!isNaN(value) && value > 0) {
      this.customMaxSpeedupMap[regionId] = value;
    } else {
      delete this.customMaxSpeedupMap[regionId];
    }

    const newMaxValue =
    this.customMaxSpeedupMap[regionId] ?? this.autoMaxSpeedupMap[regionId];

    // Atualiza direto no gráfico (sem rebuild)
    this.updateSpeedupVisualMap(regionId, newMaxValue);

    if (popover) {
      popover.open = false;
    }

  }

  private updateSpeedupVisualMap(regionId: string, max: number) {
    const chartKey = 'speedup';
    const uniqueKey = `${chartKey}-${regionId}`;

    const instance = this.chartInstances[uniqueKey];

    if (instance) {
      instance.setOption({
        visualMap: {
          max
        }
      });
    }

    this.updateStoredOptions(regionId, max);
    this.updateComparisonStoredOptions(regionId, max);

    if (this.expandedChart?.chartKey === chartKey && this.expandedChart.regionId === regionId) {
      this.expandedChartInstance?.setOption({
        visualMap: {
          max
        }
      });
    }

  }

  private updateStoredOptions(regionId: string, max: number) {
    const idx = this.chartDefinitions.findIndex(def => def.key === 'speedup');
    if (idx === -1) return;

    // normal
    if (this.normalOptions[idx]?.visualMap) {
      this.normalOptions[idx].visualMap = {
        ...this.normalOptions[idx].visualMap,
        max
       };
    }

    // smoothed
    if (this.normalSmoothedOptions[idx]?.visualMap) {
      this.normalSmoothedOptions[idx].visualMap = {
        ...this.normalSmoothedOptions[idx].visualMap,
        max
      };
    }
  }

  private updateComparisonStoredOptions(regionId: string, max: number) {
    this.comparisonCharts.forEach(comp => {
      if (comp.key !== 'speedup') return;

      comp.regionData.forEach(region => {
        if (region.regionId !== regionId) return;

        const opts = region.options;
        if (!opts) return;

        // NORMAL
        if (opts.normal?.visualMap) {
          opts.normal.visualMap = {
            ...opts.normal.visualMap,
            max
          };
        }

        // SMOOTHED (esse é o bug principal)
        if (opts.smoothed?.visualMap) {
          opts.smoothed.visualMap = {
            ...opts.smoothed.visualMap,
            max
          };
        }

        // (segurança extra)
        if (opts.relative?.visualMap) {
          opts.relative.visualMap = {
            ...opts.relative.visualMap,
            max
          };
        }

        if (opts.relativeSmoothed?.visualMap) {
          opts.relativeSmoothed.visualMap = {
            ...opts.relativeSmoothed.visualMap,
            max
          };
        }
      });
    });
  }

  // --- LÓGICA DE INSTÂNCIA E DOWNLOAD ---

  onChartInit(ecInstance: any, chartKey: string, regionId: string) {
    const uniqueKey = `${chartKey}-${regionId}`;
    this.chartInstances[uniqueKey] = ecInstance;
  }

  onExpandedChartInit(ecInstance: any): void {
    this.expandedChartInstance = ecInstance;
    requestAnimationFrame(() => this.expandedChartInstance?.resize());
  }

  openSingleChartExpansion(index: number): void {
    const chartDef = this.chartDefinitions[index];
    const regionId = this.selectedRegions[0];

    if (!chartDef || !regionId) return;

    this.expandedChart = {
      chartKey: chartDef.key,
      regionId,
      title: `${chartDef.title} · ${this.getRegionDisplayName(regionId)}`,
      kind: 'single',
      singleIndex: index
    };
    this.expandedChartOption = this.buildExpandedChartOption(this.determineChartOption(index));

    requestAnimationFrame(() => this.expandedChartInstance?.resize());
  }

  openComparisonChartExpansion(comp: ComparisonChart, region: ComparisonRegionData): void {
    this.expandedChart = {
      chartKey: comp.key,
      regionId: region.regionId,
      title: `${comp.title} · ${this.getRegionDisplayName(region.regionId)}`,
      kind: 'comparison',
      comparisonChart: comp,
      comparisonRegion: region
    };
    this.expandedChartOption = this.buildExpandedChartOption(this.determineComparisonChartOption(comp, region));

    requestAnimationFrame(() => this.expandedChartInstance?.resize());
  }

  closeExpandedChart(): void {
    this.expandedChart = null;
    this.expandedChartOption = null;
    this.expandedChartInstance = null;
  }

  private buildExpandedChartOption(option: EChartsOption | null): EChartsOption | null {
    if (!option) return null;

    const baseGrid = Array.isArray(option.grid) ? option.grid[0] : option.grid;
    const expandedGrid = baseGrid
      ? {
          ...baseGrid,
          top: 72,
          bottom: 92,
          height: undefined
        }
      : undefined;

    const baseVisualMap = Array.isArray(option.visualMap) ? option.visualMap[0] : option.visualMap;
    const expandedVisualMap = baseVisualMap
      ? {
          ...baseVisualMap,
          bottom: 20
        }
      : undefined;

    const baseTooltip = Array.isArray(option.tooltip) ? option.tooltip[0] : option.tooltip;
    const expandedTooltip: TooltipComponentOption | undefined = baseTooltip
      ? {
          ...baseTooltip,
          position: this.expandedTooltipPosition
        }
      : undefined;

    return {
      ...option,
      grid: Array.isArray(option.grid)
        ? option.grid.map((grid, index) => (index === 0 ? { ...grid, ...expandedGrid } : grid))
        : expandedGrid,
      visualMap: Array.isArray(option.visualMap)
        ? option.visualMap.map((visualMap, index) => (index === 0 ? { ...visualMap, ...expandedVisualMap } : visualMap))
        : expandedVisualMap,
      tooltip: Array.isArray(option.tooltip)
        ? option.tooltip.map((tooltip, index) => (index === 0 ? { ...tooltip, ...expandedTooltip } : tooltip))
        : expandedTooltip
    };
  }

  async prepareDownload(chartKey: string, regionId: string, format: 'png' | 'pdf' | 'svg', popover?: HTMLDetailsElement): Promise<void> {
    if (popover) popover.open = false;

    const uniqueKey = `${chartKey}-${regionId}`;
    const instance = this.chartInstances[uniqueKey];

    if (!instance) return;

    if (format === 'png') {
      this.downloadPng(instance, chartKey as ChartKey, regionId);
      return;
    }

    const option = this.resolveChartOption(chartKey as ChartKey, regionId);
    if (!option) return;

    if (format === 'svg') {
      await this.downloadSvg(
        option,
        chartKey as ChartKey,
        regionId,
        instance.getWidth(),
        instance.getHeight()
      );
      return;
    }

    await this.downloadVectorPdf(
      option,
      chartKey as ChartKey,
      regionId,
      instance.getWidth(),
      instance.getHeight()
    );
  }

  async prepareExpandedDownload(format: 'png' | 'pdf' | 'svg', popover?: HTMLDetailsElement): Promise<void> {
    if (popover) popover.open = false;
    if (!this.expandedChart || !this.expandedChartInstance) return;

    const { chartKey, regionId } = this.expandedChart;

    if (format === 'png') {
      this.downloadPng(this.expandedChartInstance, chartKey, regionId);
      return;
    }

    if (!this.expandedChartOption) return;

    if (format === 'svg') {
      await this.downloadSvg(
        this.expandedChartOption,
        chartKey,
        regionId,
        this.expandedChartInstance.getWidth(),
        this.expandedChartInstance.getHeight()
      );
      return;
    }

    await this.downloadVectorPdf(
      this.expandedChartOption,
      chartKey,
      regionId,
      this.expandedChartInstance.getWidth(),
      this.expandedChartInstance.getHeight()
    );
  }

  private getFriendlyChartName(key: ChartKey): string {
    if (isEnergyChartKey(key)) {
      return getEnergySensorName(key).replace(/[^a-z0-9]+/gi, '_');
    }

    const names: Partial<Record<ChartKey, string>> = {
      eff: 'Efficiency',
      sca: 'Scalability',
      str: 'Strong_Scalability',
      wka: 'Weak_Scalability',
      imb: 'Load_Imbalance',
      speedup: 'Speedup',
      loss: 'Speedup_Loss',
      perf: 'Performance'
    };
    return names[key] ?? 'Chart';
  }

  private downloadPng(instance: any, chartKey: ChartKey, regionId: string): void {
    const fileName = `${this.buildChartFileBaseName(chartKey, regionId)}.png`;
    const dataUrl = instance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff'
    });

    this.triggerDownload(dataUrl, fileName);
  }

  private async downloadVectorPdf(
    option: EChartsOption,
    chartKey: ChartKey,
    regionId: string,
    width: number,
    height: number
  ): Promise<void> {
    const { init } = await import('echarts');
    const container = document.createElement('div');
    const normalizedWidth = Math.max(1, Math.round(width));
    const normalizedHeight = Math.max(1, Math.round(height));
    const fileName = `${this.buildChartFileBaseName(chartKey, regionId)}.pdf`;

    Object.assign(container.style, {
      position: 'fixed',
      left: '-10000px',
      top: '0',
      width: `${normalizedWidth}px`,
      height: `${normalizedHeight}px`,
      opacity: '0',
      pointerEvents: 'none'
    });

    document.body.appendChild(container);

    let svgChart: any;

    try {
      const { chart, svgString, svgElement } = this.createSvgChartArtifacts(
        init,
        container,
        option,
        normalizedWidth,
        normalizedHeight
      );
      svgChart = chart;

      const pdf = new jsPDF({
        orientation: normalizedWidth >= normalizedHeight ? 'landscape' : 'portrait',
        unit: 'pt',
        format: [normalizedWidth, normalizedHeight]
      });

      await svg2pdf(svgElement, pdf, {
        x: 0,
        y: 0,
        width: normalizedWidth,
        height: normalizedHeight
      });

      pdf.save(fileName);
    } finally {
      svgChart?.dispose();
      container.remove();
    }
  }

  private async downloadSvg(
    option: EChartsOption,
    chartKey: ChartKey,
    regionId: string,
    width: number,
    height: number
  ): Promise<void> {
    const { init } = await import('echarts');
    const container = document.createElement('div');
    const normalizedWidth = Math.max(1, Math.round(width));
    const normalizedHeight = Math.max(1, Math.round(height));
    const fileName = `${this.buildChartFileBaseName(chartKey, regionId)}.svg`;

    Object.assign(container.style, {
      position: 'fixed',
      left: '-10000px',
      top: '0',
      width: `${normalizedWidth}px`,
      height: `${normalizedHeight}px`,
      opacity: '0',
      pointerEvents: 'none'
    });

    document.body.appendChild(container);

    let svgChart: any;

    try {
      const { chart, svgString } = this.createSvgChartArtifacts(
        init,
        container,
        option,
        normalizedWidth,
        normalizedHeight
      );
      svgChart = chart;

      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      try {
        this.triggerDownload(url, fileName);
      } finally {
        URL.revokeObjectURL(url);
      }
    } finally {
      svgChart?.dispose();
      container.remove();
    }
  }

  private createSvgChartArtifacts(
    init: (dom?: HTMLElement | null, theme?: string | object | null, opts?: EChartsInitOpts) => EChartsType,
    container: HTMLDivElement,
    option: EChartsOption,
    width: number,
    height: number
  ): { chart: EChartsType; svgString: string; svgElement: SVGSVGElement } {
    const chart = init(container, undefined, {
      renderer: 'svg',
      width,
      height
    });

    chart.setOption(option, true);

    const svgString = chart.renderToSVGString();
    const svgElement = new DOMParser()
      .parseFromString(svgString, 'image/svg+xml')
      .documentElement as unknown as SVGSVGElement;

    return { chart, svgString, svgElement };
  }

  private buildChartFileBaseName(chartKey: ChartKey, regionId: string): string {
    const chartTitle = this.getFriendlyChartName(chartKey);
    const regionName = this.getRegionDisplayName(regionId).replace(/\s+/g, '_');
    return `${chartTitle}_${regionName}`;
  }

  private triggerDownload(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  }

  private resolveChartOption(chartKey: ChartKey, regionId: string): EChartsOption | null {
    if (this.selectedRegions.length === 1 && this.selectedRegions[0] === regionId) {
      const chartIndex = this.chartDefinitions.findIndex(def => def.key === chartKey);
      return chartIndex >= 0 ? this.determineChartOption(chartIndex) : null;
    }

    for (const comparisonChart of this.comparisonCharts) {
      if (comparisonChart.key !== chartKey) continue;

      const region = comparisonChart.regionData.find(item => item.regionId === regionId);
      if (!region) continue;

      return this.determineComparisonChartOption(comparisonChart, region);
    }

    return null;
  }

  // --- FIM DA LÓGICA DE DOWNLOAD ---

  // Helper para facilitar o uso no HTML
  getCustomMaxForRegion(regionId: string): number {
    return this.customMaxSpeedupMap[regionId]
    ?? this.autoMaxSpeedupMap[regionId]
    ?? 1;

  }

  onFileInputChange(evt: Event): void {
    const file = (evt.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.processSelectedFile(file);
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.processSelectedFile(file);
  }

  private processSelectedFile(file: File): void {
    const isJsonFile = file.type === 'application/json' || file.name.toLowerCase().endsWith('.json');
    if (!isJsonFile) {
      this.error = 'Please upload a valid JSON file.';
      this.executionDetails = EMPTY_EXECUTION_DETAILS;
      this.baselineWarning = null;
      this.baselineMode = null;
      this.analysisStateService.notifyFileUploaded(false);
      this.analysisStateService.clearUploadedFile();
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        this.error = '';
        const json = JSON.parse(reader.result as string);
        const parserOutput: ParserOutput = this.parser.convertAnalyzer2Viewer(json);
        this.measurements = parserOutput.measurements;
        this.arguments = parserOutput.arguments;
        this.executionDetails = parserOutput.executionDetails;
        this.hybridViewMode = 'total-cores';
        this.sensors = parserOutput.sensors ?? [];
        this.isFileUploaded = true;
        this.analysisStateService.setUploadedFile(file.name, json);
        this.analysisStateService.notifyFileUploaded(true);

        const regions = Array.from(new Set(this.measurements.map(m => m.region)));
        this.regionDetailsMap = {};
        regions.forEach(regionId => {
          const regs = this.measurements.filter(m => m.region === regionId);
          const execs = regs.map(m => m.executionPercentage).filter(v => v !== undefined);
          if (regs.length > 0 && !this.regionDetailsMap[regionId]) {
            this.regionDetailsMap[regionId] = { filename: regs[0].filename, lines: regs[0].lines };
          }

          const byConfig = regs.reduce((acc, m) => {
            const key = `${m.parallelism.configKey}:${m.workload}`;
            (acc[key] = acc[key] || []).push(m.timeVariation);
            return acc;
          }, {} as Record<string, number[]>);

          const medians = Object.values(byConfig).map(arr => {
            if (arr.length === 0) return 0;
            arr.sort((a, b) => a - b);
            const mid = Math.floor(arr.length / 2);
            return arr.length % 2 === 0 ? (arr[mid - 1] + arr[mid]) / 2 : arr[mid];
          });

          this.metricsByRegion[regionId] = {
            executionRange: execs.length > 0 ? [ Math.min(...execs), Math.max(...execs) ] : [0, 0],
            timeVariationRange: medians.length > 0 ? [ Math.min(...medians), Math.max(...medians) ] : [0, 0]
          };
        });

        this.availableRegions = Array.from(new Set(this.measurements.map(m => m.region))).sort();
        if (this.availableRegions.length > 0) {
          const defaultRegion = this.availableRegions.find(r => r === '0') || this.availableRegions[0];
          this.onRegionSelected([defaultRegion]);
        }
      } catch (e) {
        const msg = (e as Error).message ?? '';
        if (msg.includes('Missing sequential baseline')){
          this.baselineWarning = msg;
          this.baselineMode = this.executionDetails?.parallelismMode ?? null;
        }
        this.error = `Error parsing JSON: ${(e as Error).message}`;
        this.executionDetails = EMPTY_EXECUTION_DETAILS;
        this.analysisStateService.notifyFileUploaded(false);
        this.analysisStateService.clearUploadedFile();
        this.clearAllChartDataAndStates();
      }
    };
    reader.readAsText(file);
  }

  onRegionSelected(newRegions: string[]): void {
    this.selectedRegions = newRegions;
    this.clearAllChartDataAndStates();
    this.selectedRegionDetails = null;

    if (this.measurements.length > 0 && newRegions.length > 0) {
      if (newRegions.length === 1) {
        const regionId = newRegions[0];
        this.selectedRegionDetails = this.measurements.find(m => m.region === regionId) ?? null;
        this.buildAllCharts(this.measurements, regionId);
      } else if (newRegions.length > 1) {
        this.buildComparisonCharts(this.measurements, newRegions);
      }
      this.publishSelectionSummary();
    } else {
      this.analysisStateService.updateRegionSelection(null);
    }
  }

  getRegionDisplayName(regionId: string): string {
    if (regionId === '0') return 'Whole Program (Region 0)';
    return `Region ${regionId}`;
  }

  private publishSelectionSummary(): void {
    if (!this.isFileUploaded || this.selectedRegions.length === 0) {
      this.analysisStateService.updateRegionSelection(null);
      return;
    }
    const regions: RegionSelectionRegion[] = this.selectedRegions.map(regionId => {
      const details = this.regionDetailsMap[regionId];
      const metrics = this.metricsByRegion[regionId];
      return {
        regionId,
        label: this.getRegionDisplayName(regionId),
        filename: details?.filename ?? '—',
        lines: details?.lines ?? '—',
        executionRange: metrics?.executionRange,
        timeVariationRange: metrics?.timeVariationRange
      };
    });
    const summary: RegionSelectionSummary = {
      arguments: [...this.arguments],
      executionDetails: this.executionDetails,
      regions
    };
    this.analysisStateService.updateRegionSelection(summary);
  }

  private buildComparisonCharts(meas: Measurement[], regionIds: string[]) {
    this.clearAllChartDataAndStates();
    const allRegionCharts = new Map<string, Record<string, ComparisonChartOptions>>();
    regionIds.forEach(id => {
      allRegionCharts.set(id, this.generateChartsForRegion(meas, id));
    });

    const comparisonDefs = this.chartMetadata
      .getChartDefinitions(1, 1, 0, this.sensors)
      .map(({ key, title }) => ({ key, title }));

    this.comparisonCharts = comparisonDefs.map(def => {
      const key = def.key;
      const regionData: ComparisonRegionData[] = regionIds.map(id => {
        const regionCharts = allRegionCharts.get(id) || {};
        return {
          regionId: id,
          options: regionCharts[key] || { normal: null, smoothed: null, relative: null, relativeSmoothed: null }
        };
      });
      return { key, title: def.title, regionData, showSmoothed: false, showRelative: false, showEDP: false };
    });
  }

  private generateChartsForRegion(meas: Measurement[], regionId: string): Record<string, ComparisonChartOptions> {
    const allCharts: Record<string, ComparisonChartOptions> = {};
    let region: RegionMatrices | null;
    try { 
      region = buildRegion(regionId, meas, { hybridViewMode: this.hybridViewMode }); 
    } 
    catch (e) {
      if (e instanceof MissingSequentialBaselineError) {
        this.baselineWarning = e.message;
        this.baselineMode = e.parallelismMode;
      }
      return {};
    }
    if (!region || !region.efficiencies || region.efficiencies.length === 0 || region.columns.length <= 1) return {};

    const context = this.createChartBuildContext(region);
    const { finalMax, maxLossCalculated, minLossCalculated } = this.getScaleValues(regionId, context);
    const defs = this.chartMetadata.getChartDefinitions(finalMax, maxLossCalculated, minLossCalculated, this.sensors);

    defs.forEach(def => {
      allCharts[def.key] = this.buildChartOptions(def, context);
    });
    return allCharts;
  }
  
  private buildAllCharts(meas: Measurement[], regionId: string) {
    this.clearAllChartDataAndStates();
    let region: RegionMatrices | null = null;
    try { 
      region = buildRegion(regionId, meas, { hybridViewMode: this.hybridViewMode }); 
    } 
    catch (e) {
      if (e instanceof MissingSequentialBaselineError) {
        this.baselineWarning = e.message;
        this.baselineMode = e.parallelismMode;
      }
      return;
    }
    if (!region || !region.efficiencies || region.efficiencies.length === 0 || region.columns.length <=1) return;

    const context = this.createChartBuildContext(region);
    const { finalMax, maxLossCalculated, minLossCalculated } = this.getScaleValues(regionId, context);
    const hasImbalanceData = context.imbalanceMatrix.length > 0;

    this.chartDefinitions = this.chartMetadata
      .getChartDefinitions(finalMax, maxLossCalculated, minLossCalculated, this.sensors)
      .filter(def => def.key !== 'imb' || hasImbalanceData)
      .filter(def => !isEnergyChartKey(def.key) || !!context.energyByKey[def.key]);

    this.chartDefinitions.forEach(def => {
      const options = this.buildChartOptions(def, context);
      this.normalOptions.push(options.normal ?? {} as EChartsOption);
      this.normalSmoothedOptions.push(options.smoothed ?? {} as EChartsOption);
      this.relativeOptions.push(options.relative ?? { ...this.normalOptions[this.normalOptions.length - 1] });
      this.relativeSmoothedOptions.push(options.relativeSmoothed ?? { ...this.normalSmoothedOptions[this.normalSmoothedOptions.length - 1] });
    });

    this.showSmoothed = this.chartDefinitions.map(() => false);
    this.showRelative = this.chartDefinitions.map(() => false);
    this.showEDP = this.chartDefinitions.map(() => false);
  }

  private getChartLabels(region: RegionMatrices) {
    return {
      W: region.rows,
      T: region.columns,
      W_minus_1: region.rows.length > 1 ? region.rows.slice(1) : [],
      T_minus_1: region.columns.length > 1 ? region.columns.slice(1) : [],
    };
  }

  private buildParallelismExtraFields(
    region: RegionMatrices,
    rowConfigs: RegionMatrices['parallelConfigs'],
    columnCount: number
  ): HeatmapExtraFields {
    if (rowConfigs.length === 0 || columnCount === 0) {
      return {};
    }

    const fields: HeatmapExtraFields = {
      'Execution model': this.expandStaticTooltipValue(rowConfigs, columnCount, this.getExecutionModelLabel(region.parallelismMode))
    };

    switch (region.parallelismMode) {
      case 'mpi':
        fields['Parallel axis'] = this.expandStaticTooltipValue(rowConfigs, columnCount, 'MPI ranks');
        fields['MPI ranks'] = this.expandParallelConfigMetric(rowConfigs, columnCount, config => config.ranks ?? config.idealParallelism);
        break;
      case 'omp':
        fields['Parallel axis'] = this.expandStaticTooltipValue(rowConfigs, columnCount, 'OpenMP cores / threads');
        fields['Cores'] = this.expandParallelConfigMetric(rowConfigs, columnCount, config => config.totalCores);
        break;
      case 'hybrid':
        fields['View mode'] = this.expandStaticTooltipValue(
          rowConfigs,
          columnCount,
          this.hybridViewMode === 'total-cores' ? 'Heatmap view' : 'Topology view'
        );
        if (this.hybridViewMode === 'total-cores') {
          fields['Parallel axis'] = this.expandStaticTooltipValue(rowConfigs, columnCount, 'Total cores');
          fields['Total cores'] = this.expandParallelConfigMetric(rowConfigs, columnCount, config => config.totalCores);
          fields['Contributing topologies'] = this.expandParallelConfigMetric(rowConfigs, columnCount, config => config.topologies.join(', '));
        } else {
          fields['Parallel axis'] = this.expandStaticTooltipValue(rowConfigs, columnCount, 'MPI ranks x cores/rank');
          fields['MPI ranks'] = this.expandParallelConfigMetric(rowConfigs, columnCount, config => config.ranks ?? 0);
          fields['Cores / rank'] = this.expandParallelConfigMetric(rowConfigs, columnCount, config => config.cores ?? 0);
          fields['Total cores'] = this.expandParallelConfigMetric(rowConfigs, columnCount, config => config.totalCores);
        }
        break;
      default:
        fields['Parallel axis'] = this.expandStaticTooltipValue(rowConfigs, columnCount, 'Parallel configuration');
        break;
    }

    return fields;
  }

  private expandParallelConfigMetric(
    rowConfigs: RegionMatrices['parallelConfigs'],
    columnCount: number,
    selector: (config: RegionMatrices['parallelConfigs'][number]) => number | string
  ): Array<Array<number | string>> {
    return rowConfigs.map(config => Array(columnCount).fill(selector(config)));
  }

  private expandStaticTooltipValue(
    rowConfigs: RegionMatrices['parallelConfigs'],
    columnCount: number,
    value: string
  ): Array<Array<number | string>> {
    return rowConfigs.map(() => Array(columnCount).fill(value));
  }

  private getExecutionModelLabel(mode: ParallelismMode): string {
    switch (mode) {
      case 'mpi':
        return 'MPI';
      case 'omp':
        return 'OpenMP';
      case 'hybrid':
        return 'Hybrid (MPI + OpenMP)';
      default:
        return 'Unknown';
    }
  }

  private getParallelAxisLabel(region: RegionMatrices): string {
    switch (region.parallelismMode) {
      case 'mpi':
        return 'MPI ranks';
      case 'omp':
        return 'Cores';
      case 'hybrid':
        return this.hybridViewMode === 'total-cores' ? 'Total cores' : 'MPI ranks x cores/rank';
      default:
        return 'Parallel config';
    }
  }

  private getTooltipValueLabel(def: ChartDefinition, type: ChartMode): string {
    if (isEnergyChartKey(def.key)) {
      return type === 'edp' ? 'EDP' : 'Normalized energy';
    }

    if (type === 'relative') {
      return `Relative ${def.title}`;
    }

    return def.title;
  }

  private createChartBuildContext(region: RegionMatrices): ChartBuildContext {
    const { strong, scalab, weak } = buildScalabilityMatrices(region);
    const { relativeStrong, relativeScalab, relativeWeak } = buildRelativeScalabilityMatrices(region);
    const parTime = this.heatmap.transpose(region.times);
    const seqTime = this.heatmap.transpose(region.seqTimes.map(t => Array(region.columns.length).fill(t)));
    const energyByKey: Record<string, EnergyChartMatrices> = {};

    (region.energies ?? []).forEach(energy => {
      const rawEdpByWorkload = this.multiplyMatrices(energy.values, region.times);
      energyByKey[buildEnergyChartKey(energy.name)] = {
        rawEnergy: this.heatmap.transpose(energy.values),
        normalizedEnergy: this.heatmap.transpose(this.normalizeRows(energy.values)),
        rawEdp: this.heatmap.transpose(rawEdpByWorkload),
        normalizedEdp: this.heatmap.transpose(this.normalizeRows(rawEdpByWorkload))
      };
    });

    return {
      region,
      efficiencyMatrix: this.heatmap.transpose(region.efficiencies),
      imbalanceMatrix: region.imbalances.length > 0 ? this.heatmap.transpose(region.imbalances) : [],
      strong,
      scalab,
      weak,
      relativeStrong,
      relativeScalab,
      relativeWeak,
      speedupMatrix: this.heatmap.transpose(region.speedups),
      lossMatrix: this.heatmap.transpose(region.speedupLosses),
      parTime,
      seqTime,
      normalizedPerformance: this.heatmap.transpose(
        this.normalizeRows(region.times.map(row => row.map(value => value > 0 ? 1 / value : 0)))
      ),
      energyByKey
    };
  }

  private getScaleValues(regionId: string, context: ChartBuildContext) {
    const lossValues = context.lossMatrix.slice(1).flat();
    const maxLossCalculated = Math.ceil(this.maxFinite(lossValues, 0));
    const minLossCalculated = Math.floor(this.minFinite(lossValues, 0));

    this.autoMaxSpeedupMap[regionId] = Math.max(1, Math.ceil(this.maxFinite(context.speedupMatrix.flat(), 1)));
    const finalMax = this.customMaxSpeedupMap[regionId] ?? this.autoMaxSpeedupMap[regionId];

    return { finalMax, maxLossCalculated, minLossCalculated };
  }

  private buildChartOptions(def: ChartDefinition, context: ChartBuildContext): ComparisonChartOptions {
    const options: ComparisonChartOptions = { normal: null, smoothed: null, relative: null, relativeSmoothed: null };
    const normalParams = this.getHeatmapParams(def, 'normal', context);

    if (normalParams) {
      options.normal = this.heatmap.buildHeatmapOption(normalParams);
      const smoothParams = this.getSmoothedHeatmapParams(def, 'normal', normalParams, context);
      options.smoothed = smoothParams ? this.heatmap.buildSmoothedHeatmapOption(smoothParams) : null;
    }

    if (this.hasAlternativeChartMode(def.key)) {
      const alternativeMode: ChartMode = isEnergyChartKey(def.key) ? 'edp' : 'relative';
      const relativeParams = this.getHeatmapParams(def, alternativeMode, context);

      if (relativeParams) {
        options.relative = this.heatmap.buildHeatmapOption(relativeParams);
        const smoothRelativeParams = this.getSmoothedHeatmapParams(def, alternativeMode, relativeParams, context);
        options.relativeSmoothed = smoothRelativeParams ? this.heatmap.buildSmoothedHeatmapOption(smoothRelativeParams) : null;
      }
    }

    return options;
  }

  private getHeatmapParams(def: ChartDefinition, type: ChartMode, context: ChartBuildContext): HeatmapParams | null {
    const key = def.key;
    const labels = this.getChartLabels(context.region);
    let matrix: number[][] = [];
    let rows: string[] = [];
    let columns: string[] = [];
    let rowConfigs = context.region.parallelConfigs;
    let title = type === 'relative' ? `Relative ${def.title.toLowerCase()}` : def.title;

    switch (key) {
      case 'eff':
        matrix = this.toPercentMatrix(context.efficiencyMatrix.slice(1));
        rows = labels.T_minus_1.map(String); columns = labels.W.map(String);
        rowConfigs = context.region.parallelConfigs.slice(1);
        break;
      case 'speedup':
        matrix = context.speedupMatrix.slice(1);
        rows = labels.T_minus_1.map(String); 
        columns = labels.W.map(String);
        rowConfigs = context.region.parallelConfigs.slice(1);
        break;
      case 'loss':
        matrix = context.lossMatrix.slice(1);
        rows = labels.T_minus_1.map(String); 
        columns = labels.W.map(String);
        rowConfigs = context.region.parallelConfigs.slice(1);
        break;
      case 'imb':
        if (context.imbalanceMatrix.length === 0) return null;
        matrix = this.toPercentMatrix(context.imbalanceMatrix.slice(1));
        rows = labels.T_minus_1.map(String); columns = labels.W.map(String);
        rowConfigs = context.region.parallelConfigs.slice(1);
        break;
      case 'str':
        matrix = this.heatmap.transpose(this.toPercentMatrix(type === 'relative' ? context.relativeStrong : context.strong));
        rows = labels.T_minus_1.map(String); columns = labels.W.map(String);
        rowConfigs = context.region.parallelConfigs.slice(1);
        break;
      case 'sca':
        const scaPct = (type === 'relative' ? context.relativeScalab : context.scalab)
          .map(r => r.slice(1).map(v => v * 100));
        if (scaPct.length === 0 || scaPct[0].length === 0) return null;
        matrix = this.heatmap.transpose(scaPct);
        rows = labels.T_minus_1.map(String); columns = labels.W_minus_1.map(String);
        rowConfigs = context.region.parallelConfigs.slice(1);
        break;
      case 'wka':
        const weakPct = (type === 'relative' ? context.relativeWeak : context.weak).map(r => r.map(v => v * 100));
        if (weakPct.length === 0 || weakPct[0].length === 0) return null;
        matrix = this.heatmap.transpose(weakPct);
        rows = labels.T_minus_1.map(String); columns = labels.W_minus_1.map(String);
        rowConfigs = context.region.parallelConfigs.slice(1);
        break;
      case 'perf':
        matrix = this.toPercentMatrix(context.normalizedPerformance);
        rows = labels.T.map(String); columns = labels.W.map(String);
        break;
      default:
        if (isEnergyChartKey(key)) {
          const energy = context.energyByKey[key];
          if (!energy) return null;
          matrix = this.toPercentMatrix(type === 'edp' ? energy.normalizedEdp : energy.normalizedEnergy);
          rows = labels.T.map(String); columns = labels.W.map(String);
          title = type === 'edp' ? `EDP ${def.title}` : def.title;
        }
        break;
    }

    if (matrix.length === 0 || rows.length === 0 || columns.length === 0) return null;

    const parallelismExtraFields = this.buildParallelismExtraFields(context.region, rowConfigs, columns.length);
    const params: HeatmapParams = {
      matrix,
      rows,
      columns,
      title,
      palette: def.palette,
      min: def.min,
      max: def.max,
      unit: def.unit,
      xAxisLabel: 'Workload',
      yAxisLabel: this.getParallelAxisLabel(context.region),
      tooltipValueLabel: this.getTooltipValueLabel(def, type)
    };

    if ((key === 'eff' || key === 'speedup') && type === 'normal') {
      params.extraFields = {
        ...parallelismExtraFields,
        'Parallel runtime (s)': context.parTime.slice(1),
        'Sequential baseline runtime (s)': context.seqTime.slice(1)
      };
    } else if (key === 'perf') {
      params.extraFields = {
        ...parallelismExtraFields,
        'Runtime (s)': context.parTime
      };
    } else if (isEnergyChartKey(key)) {
      const energy = context.energyByKey[key];
      params.extraFields = type === 'edp'
        ? { ...parallelismExtraFields, EDP: energy.rawEdp }
        : { ...parallelismExtraFields, 'Energy (J)': energy.rawEnergy };
    } else if (Object.keys(parallelismExtraFields).length > 0) {
      params.extraFields = parallelismExtraFields;
    }
    return params;
  }

  private getSmoothedHeatmapParams(def: ChartDefinition, type: ChartMode, baseParams: HeatmapParams, context: ChartBuildContext) {
    const key = def.key;
    let rawMatrix: number[][] = [];
     switch (key) {
      case 'eff': rawMatrix = context.efficiencyMatrix.slice(1); break;
      case 'speedup': if (context.speedupMatrix.length > 0) rawMatrix = context.speedupMatrix.slice(1); break;
      case 'loss': 
        if (context.lossMatrix.length > 0) rawMatrix = context.lossMatrix.slice(1);
        break;
      case 'imb': if (context.imbalanceMatrix.length > 0) rawMatrix = context.imbalanceMatrix.slice(1); break;
      case 'str': rawMatrix = this.heatmap.transpose(type === 'relative' ? context.relativeStrong : context.strong); break;
      case 'sca': rawMatrix = this.heatmap.transpose((type === 'relative' ? context.relativeScalab : context.scalab).map(r => r.slice(1))); break;
      case 'wka': rawMatrix = this.heatmap.transpose(type === 'relative' ? context.relativeWeak : context.weak); break;
      case 'perf': rawMatrix = context.normalizedPerformance; break;
      default:
        if (isEnergyChartKey(key)) {
          const energy = context.energyByKey[key];
          rawMatrix = type === 'edp' ? energy?.normalizedEdp ?? [] : energy?.normalizedEnergy ?? [];
        }
        break;
    }
    if (rawMatrix.length === 0 || rawMatrix[0].length === 0) return null;
    return {
      matrix: rawMatrix,
      originalRows: baseParams.rows,
      originalColumns: baseParams.columns,
      palette: def.palette,
      min: def.min,
      max: def.max,
      title: baseParams.title,
      unit: def.unit,
      size: 50,
      isPercentage: key !== 'speedup' && key !== 'loss' // Speedup e Loss são medidas 'X', não '%'
    };
  }

  private hasAlternativeChartMode(key: ChartKey): boolean {
    return this.isScalabilityKey(key) || isEnergyChartKey(key);
  }

  private isScalabilityKey(key: string | undefined): key is 'sca' | 'str' | 'wka' {
    return key === 'sca' || key === 'str' || key === 'wka';
  }

  private toPercentMatrix(matrix: number[][]): number[][] {
    return matrix.map(row => row.map(value => this.toFinite(value) * 100));
  }

  private normalizeRows(matrix: number[][]): number[][] {
    return matrix.map(row => {
      const finiteValues = row.filter(Number.isFinite);
      if (finiteValues.length === 0) return row.map(() => 0);

      const min = Math.min(...finiteValues);
      const max = Math.max(...finiteValues);
      const range = max - min;

      if (range === 0) return row.map(() => 0);
      return row.map(value => Number.isFinite(value) ? (value - min) / range : 0);
    });
  }

  private multiplyMatrices(left: number[][], right: number[][]): number[][] {
    return left.map((row, rowIndex) =>
      row.map((value, columnIndex) => this.toFinite(value) * this.toFinite(right[rowIndex]?.[columnIndex]))
    );
  }

  private maxFinite(values: number[], fallback: number): number {
    const finiteValues = values.filter(Number.isFinite);
    return finiteValues.length > 0 ? Math.max(...finiteValues) : fallback;
  }

  private minFinite(values: number[], fallback: number): number {
    const finiteValues = values.filter(Number.isFinite);
    return finiteValues.length > 0 ? Math.min(...finiteValues) : fallback;
  }

  private toFinite(value: unknown): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  toggleSmooth(idx: number): void {
    if (this.showSmoothed.length > idx) this.showSmoothed[idx] = !this.showSmoothed[idx];
  }

  toggleScalabilityMode(idx: number): void {
    const key = this.chartDefinitions[idx]?.key;
    if (this.showRelative.length > idx && this.isScalabilityKey(key)) {
      this.showRelative[idx] = !this.showRelative[idx];
    }
  }

  toggleSensorMode(idx: number): void {
    const key = this.chartDefinitions[idx]?.key;
    if (this.showEDP.length > idx && key && isEnergyChartKey(key)) {
      this.showEDP[idx] = !this.showEDP[idx];
    }
  }

  isHybridAnalysis(): boolean {
    return this.executionDetails.parallelismMode === 'hybrid';
  }

  setHybridViewMode(mode: HybridViewMode): void {
    if (this.hybridViewMode === mode) return;
    this.hybridViewMode = mode;

    if (this.selectedRegions.length > 0) {
      this.onRegionSelected([...this.selectedRegions]);
    }
  }

  getHybridViewDescription(): string {
    return this.hybridViewMode === 'total-cores'
      ? 'Rows are grouped by total cores. Tooltips keep the contributing topologies.'
      : 'Rows preserve each MPI x OpenMP topology as distinct parallel configurations.';
  }

  determineChartOption(idx: number): EChartsOption | null {
    if (!this.chartDefinitions || idx >= this.chartDefinitions.length) return null;
    const chartDef = this.chartDefinitions[idx];
    const isRelative = this.showRelative[idx];
    const isEDP = this.showEDP[idx];
    const isSmoothed = this.showSmoothed[idx];
    let option: EChartsOption | null = null;
    
    if (isEnergyChartKey(chartDef.key) && isEDP) {
      option = isSmoothed ? this.relativeSmoothedOptions[idx] : this.relativeOptions[idx];
    } else if (!this.isScalabilityKey(chartDef.key)) {
      option = isSmoothed ? this.normalSmoothedOptions[idx] : this.normalOptions[idx];
    } else {
      option = isRelative ? (isSmoothed ? this.relativeSmoothedOptions[idx] : this.relativeOptions[idx]) : (isSmoothed ? this.normalSmoothedOptions[idx] : this.normalOptions[idx]);
    }
    return (option && Object.keys(option).length > 0) ? option : null;
  }

  determineComparisonChartOption(comp: ComparisonChart, regionData: ComparisonRegionData): EChartsOption | null {
    const options = regionData.options;
    if (!options) return null;
    if (isEnergyChartKey(comp.key) && comp.showEDP) return comp.showSmoothed ? options.relativeSmoothed : options.relative;
    if (!this.isScalabilityKey(comp.key)) return comp.showSmoothed ? options.smoothed : options.normal;
    if (comp.showRelative) return comp.showSmoothed ? options.relativeSmoothed : options.relative;
    return comp.showSmoothed ? options.smoothed : options.normal;
  }

  hasNoLoadImbalanceData(regionData: ComparisonRegionData): boolean {
    const options = regionData.options;
    if (!options) return true;
    return !options.normal && !options.smoothed;
  }

  isScalabilityChartComparison(key: string): boolean {
    return this.isScalabilityKey(key);
  }

  isSensorChartComparison(key: string): boolean {
    return isEnergyChartKey(key);
  }

  isScalabilityChart(index: number): boolean {
    const key = this.chartDefinitions[index]?.key;
    return this.chartDefinitions && index < this.chartDefinitions.length && this.isScalabilityKey(key);
  }

  isSensorChart(index: number): boolean {
    const key = this.chartDefinitions[index]?.key;
    return this.chartDefinitions && index < this.chartDefinitions.length && !!key && isEnergyChartKey(key);
  }

  getChartTooltipText(key: ChartKey): string {
    return this.chartMetadata.getChartTooltipText(key, {
      parallelismMode: this.executionDetails.parallelismMode,
      hybridViewMode: this.executionDetails.parallelismMode === 'hybrid' ? this.hybridViewMode : undefined
    });
  }

  drop(event: CdkDragDrop<ComparisonRegionData[]>, comp: ComparisonChart) {
    moveItemInArray(comp.regionData, event.previousIndex, event.currentIndex);
    const syncSourceKeys: Array<ComparisonChart['key']> = ['eff', 'sca', 'str', 'wka', 'speedup', 'loss'];
    if (this.comparisonCharts.length > 0 && syncSourceKeys.includes(comp.key)) {
        const newOrder = comp.regionData.map(d => d.regionId);
        this.comparisonCharts.forEach(otherComp => {
            if (otherComp.key !== comp.key) otherComp.regionData.sort((a, b) => newOrder.indexOf(a.regionId) - newOrder.indexOf(b.regionId));
        });
    }
  }
}
